import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useContext,
} from "react";
import { SimpleDialog, Button, SimpleUtility } from "simple-ui-components";

import SpeakerGroupIcon from "@mui/icons-material/SpeakerGroup";
import DeveloperModeIcon from "@mui/icons-material/DeveloperMode";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import RoomIcon from "@mui/icons-material/Room";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FilterIcon from "@mui/icons-material/Filter";
import DetailsIcon from "@mui/icons-material/Details";
import PublishIcon from "@mui/icons-material/Publish";

import { AboutEquipment, AboutEquipmentDetail } from "../../theme";
import { AuthContext } from "../authContextProvider";
import { ReplaceWord } from "../shared/utility";
import UploadPDF from "../shared/uploadPDF";
import PDFDetail from "../routes/PDFDetail";
import DeletePopup from "../shared/deletePopup";
import { del } from "../../server-requests/serverRequests";
import { ViewPDF } from "../shared/ViewPDF";
import { getTranslation } from "../languageProvider";

export const EquipmentDetailPopup = forwardRef(
  (
    { EditEquipment, DeleteEquipment, CopyEquipment, Uploaded, Reload },
    ref
  ) => {
    const { Dialog, DialogBody, DialogFooter } = SimpleDialog;
    const { UpdateObject } = SimpleUtility;

    const deletePDFRef = useRef();
    const viewPDFRef = useRef();

    const { snackbarHandler } = useContext(AuthContext);

    const [open, setOpen] = useState(false);
    const [equipment, setEquipment] = useState("");
    const [serviceOrder, setServiceOrder] = useState({
      list: [],
      is: false,
      routePointId: "",
    });
    const [clear, setClear] = useState(false);

    // trigger from props
    useImperativeHandle(ref, () => ({
      Detail(detail, serviceOrders, routePointId) {
        setServiceOrder((prev) =>
          UpdateObject(prev, {
            list: serviceOrders ? serviceOrders : [],
            routePointId: routePointId ? routePointId : "",
            is: serviceOrders ? true : false,
          })
        );
        setOpen(true);
        setEquipment(detail);
      },
    }));
    const handleClose = () => setOpen(false);

    const editEquipmentClickedHandler = () => {
      setOpen(false);
      EditEquipment(equipment);
    };

    const deleteEquipmentClickedHandler = () => {
      setOpen(false);
      DeleteEquipment(equipment.id);
    };

    const copyEquipmentClickedHandler = () => {
      setOpen(false);
      CopyEquipment(equipment);
    };

    // upload PDF
    const uploadPDFHandler = (event) => {
      if (event) {
        if (event.target.files.length > 0) {
          let file = event.target.files[0];
          let reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onloadend = () => {
            let toSend = {
              routePointId: serviceOrder.routePointId,
              content: reader.result.replace(
                "data:application/pdf;base64,",
                ""
              ),
              originalFilename: file.name,
            };
            Uploaded(toSend);
            setClear(true);
            setOpen(false);
          };
        } else {
          setClear(true);
        }
      } else {
        setClear(true);
      }
    };

    const viewPdfClickHandler = (order) => viewPDFRef.current.Detail(order);
    const deletePDFClickHandler = (order) => {
      deletePDFRef.current.Delete(
        order,
        `${getTranslation("0032")} PDF`,
        getTranslation("0120") &&
          ReplaceWord(getTranslation("0120"), "**NAME**", "PDF")
      );
    };

    const deletePDFHandler = (toDelete) => {
      if (toDelete)
        del("routes/points/service-orders", {
          id: toDelete.id,
          routePointId: toDelete.routePointId,
        })
          .then(() => {
            snackbarHandler(
              ReplaceWord(getTranslation("0121"), "**NAME**", "PDF"),
              "success"
            );
            Reload();
            setOpen(false);
          })
          .catch((status) => snackbarHandler(getTranslation(`error${status}`)));
    };

    return (
      <>
        {open && (
          <Dialog
            theme="default"
            size="sm"
            close={handleClose}
            open={open}
            title={`${getTranslation("0108")}: ${
              equipment && equipment.serialNumber
            }`}
          >
            <DialogBody>
              {equipment && (
                <AboutEquipmentDetail color={equipment.state.color}>
                  <div className="status">
                    <div>
                      <img src={equipment.icon} alt={equipment.serialNumber} />
                    </div>
                  </div>
                  <AboutEquipment>
                    {equipment.client && (
                      <div>
                        <PersonOutlinedIcon />
                        <span>
                          <b data-translate="0072">{getTranslation("0072")}</b>
                          {equipment.client.name}
                        </span>
                      </div>
                    )}
                    {equipment.model.brand && (
                      <div>
                        <SpeakerGroupIcon />
                        <span>
                          <b data-translate="0086">{getTranslation("0086")}</b>
                          {equipment.model.brand.name}
                        </span>
                      </div>
                    )}

                    {equipment.model && (
                      <div>
                        <DeveloperModeIcon />
                        <span>
                          <b data-translate="0085">{getTranslation("0085")}</b>
                          {equipment.model.name}
                        </span>
                      </div>
                    )}

                    {equipment.state && (
                      <div>
                        {equipment.state.code === 0 && (
                          <HelpOutlineOutlinedIcon />
                        )}
                        {equipment.state.code === 1 && (
                          <CheckCircleOutlineIcon />
                        )}
                        {equipment.state.code === 2 && <HighlightOffIcon />}
                        {equipment.state.code === 3 && <BlockIcon />}
                        {equipment.state.code === 4 && <ErrorOutlinedIcon />}
                        <span>
                          <b data-translate="0109">{getTranslation("0109")}</b>
                          {equipment.state.name}
                        </span>
                      </div>
                    )}

                    {/*            <div>
                    <RoomIcon />
                    <span data-translate="0111,0112">
                      <b data-translate="0110">{getTranslation("0110")}</b>
                      {getTranslation("0111")}: {equipment.latitude}
                      <br />
                      {getTranslation("0112")}: {equipment.longitude}
                    </span>
                  </div> */}

                    {equipment.address && (
                      <div>
                        <RoomIcon />
                        <span>
                          <b data-translate="0110">{getTranslation("0110")}</b>
                          {equipment.address}
                        </span>
                      </div>
                    )}

                    {equipment.observations && (
                      <div>
                        <DetailsIcon />
                        <span>
                          <b data-translate="0016">{getTranslation("0016")}</b>
                          {equipment.observations}
                        </span>
                      </div>
                    )}
                    {serviceOrder.is && (
                      <div>
                        <PublishIcon />
                        <span>
                          <b data-translate="0169">{getTranslation("0169")}</b>
                          {serviceOrder.list.length > 0 &&
                            serviceOrder.list.map((order, index) => (
                              <PDFDetail
                                order={order}
                                key={index}
                                ViewPDF={() => viewPdfClickHandler(order)}
                                DeleteElement={
                                  <DeleteOutlineOutlinedIcon
                                    onClick={() => deletePDFClickHandler(order)}
                                    className="danger"
                                  />
                                }
                              />
                            ))}
                          {serviceOrder.list.length < 5 && (
                            <UploadPDF
                              labelName={getTranslation("0167")}
                              translate="0167"
                              Uploaded={uploadPDFHandler}
                              accept=".pdf, .txt"
                              clear={clear}
                            />
                          )}
                        </span>
                      </div>
                    )}
                  </AboutEquipment>
                </AboutEquipmentDetail>
              )}
            </DialogBody>
            <DialogFooter multiple={true}>
              {CopyEquipment && (
                <Button
                  type="button"
                  clicked={copyEquipmentClickedHandler}
                  icon={<FilterIcon />}
                  text={getTranslation("0146")}
                  className="btn-secondary"
                  theme="default"
                  style={{ marginRight: "auto" }}
                />
              )}
              {DeleteEquipment && (
                <Button
                  type="button"
                  clicked={deleteEquipmentClickedHandler}
                  icon={<DeleteOutlineOutlinedIcon />}
                  text={getTranslation("0032")}
                  className="btn-danger"
                  theme="default"
                />
              )}
              {EditEquipment && (
                <Button
                  type="button"
                  clicked={editEquipmentClickedHandler}
                  icon={<EditOutlinedIcon />}
                  text={getTranslation("0070")}
                  className="btn-primary"
                  theme="default"
                  style={{ marginLeft: "auto" }}
                />
              )}
            </DialogFooter>
          </Dialog>
        )}
        {DeleteEquipment && (
          <DeletePopup ref={deletePDFRef} DeleteSuccess={deletePDFHandler} />
        )}
        {serviceOrder.is && <ViewPDF ref={viewPDFRef} />}
      </>
    );
  }
);
